/**
 * Maneuver video text overlay user_object config ("Maneuver default").
 */

import { getData } from "./global";
import { apiEndpoints } from "../config/env";
import { error as logError } from "./console";
import { persistantStore } from "../store/persistantStore";

export const MANEUVER_TEXT_OVERLAY_TYPE = "Maneuver Text";
export const MANEUVER_VIDEO_OVERLAY_OBJECT_NAME = "Maneuver default";

export interface ManeuverTextOverlaySeriesItem {
  name: string;
  color?: string;
  height?: number;
  primaryChannelLabel?: string;
}

export interface ManeuverTextOverlayConfig {
  backgroundColor?: string;
  opacity?: number;
  orientation?: "horizontal" | "vertical";
  position?: { x: number; y: number };
  series: ManeuverTextOverlaySeriesItem[];
}

const { selectedClassName, selectedProjectId } = persistantStore;

export function readManeuverVideoOverlayName(): string {
  return MANEUVER_VIDEO_OVERLAY_OBJECT_NAME;
}

interface RawOverlayChart {
  overlayType?: string;
  backgroundColor?: string;
  opacity?: number;
  orientation?: "horizontal" | "vertical";
  position?: { x: number; y: number };
  series?: Array<{
    channel?: { name?: string };
    color?: string;
    height?: number;
    primaryChannelLabel?: string;
  }>;
}

export function filterManeuverTextOverlayCharts(chartInfo: unknown[]): RawOverlayChart[] {
  if (!Array.isArray(chartInfo)) return [];
  return chartInfo.filter(
    (chart): chart is RawOverlayChart =>
      !!chart &&
      typeof chart === "object" &&
      (chart as RawOverlayChart).overlayType === MANEUVER_TEXT_OVERLAY_TYPE
  );
}

function mapChartToOverlayConfig(chart: RawOverlayChart): ManeuverTextOverlayConfig | null {
  const series = (chart.series ?? [])
    .filter((item) => item?.channel?.name)
    .map((item) => ({
      name: item.channel!.name!,
      color: item.color,
      height: item.height,
      primaryChannelLabel: item.primaryChannelLabel,
    }));
  if (series.length === 0) return null;
  return {
    backgroundColor: chart.backgroundColor,
    opacity: chart.opacity,
    orientation: chart.orientation,
    position: chart.position,
    series,
  };
}

export async function fetchManeuverTextOverlayCharts(
  userId: number,
  objectName: string = readManeuverVideoOverlayName()
): Promise<ManeuverTextOverlayConfig[]> {
  const resolvedName = objectName.trim() || readManeuverVideoOverlayName();
  try {
    const response = await getData(
      `${apiEndpoints.app.users}/object?class_name=${encodeURIComponent(selectedClassName())}&project_id=${encodeURIComponent(selectedProjectId())}&user_id=${encodeURIComponent(userId)}&parent_name=overlay&object_name=${encodeURIComponent(resolvedName)}`
    );
    if (!response?.success || !response?.data?.chart_info) {
      if (resolvedName !== readManeuverVideoOverlayName()) {
        return fetchManeuverTextOverlayCharts(userId, readManeuverVideoOverlayName());
      }
      return [];
    }
    const charts = filterManeuverTextOverlayCharts(response.data.chart_info);
    return charts.map(mapChartToOverlayConfig).filter((c): c is ManeuverTextOverlayConfig => c !== null);
  } catch (err: unknown) {
    logError("fetchManeuverTextOverlayCharts failed", err);
    return [];
  }
}

export function applyManeuverTextOverlayNaming(
  setOverlayName: (name: string) => void,
  setChartObjectName: (name: string) => void
): void {
  const name = readManeuverVideoOverlayName();
  setOverlayName(name);
  setChartObjectName(name);
}
